// eslint-disable-next-line @typescript-eslint/ban-ts-comment

import logger from 'src/logger';
import AnimationsManager from 'src/sdk-components/AnimationsManager';
import { LoadingState } from 'src/sdk-components/VSObject';
import { getAspectRatio } from 'src/utils';
import { disposeMesh } from 'src/utils/object3DHelpers';
import { ComponentInteractionType, Inputs, Outputs, SceneComponent } from './types';

export class ImageComponent extends SceneComponent {
  private root: any = null;

  private pivot: any = null;

  private plane: any = null;

  private mixer: any = null;

  private animationsManager: AnimationsManager | null;

  private size: number[];

  public animate = false;

  inputs: Inputs = {
    width: null,
    height: null,
    period: 4,
    url: '',
    color: 0xff0033,
    enableAnimation: false,
    enableHoverAnimation: false,
    animationPulse: false,
    animationUpDown: false,
    animationFlip: false,
  };

  outputs = {
    loadingState: LoadingState.Idle,
  } as Outputs;

  // setting which events are compatible with the component
  events = {
    [ComponentInteractionType.CLICK]: true,
    [ComponentInteractionType.HOVER]: true,
    [ComponentInteractionType.DRAG]: false,
  };

  onInit() {
    const THREE = this.context.three;
    this.root = new THREE.Object3D();
    this.pivot = new THREE.Object3D();
    // setting the collider to add the click event
    this.outputs.collider = this.root;
    this.root.add(this.pivot);

    this.outputs.objectRoot = this.root;
    this.animationsManager = new AnimationsManager(THREE, this.root.parent);

    getAspectRatio(this.inputs.url)
      .then(async ([w, h]) => {
        this.outputs.loadingState = LoadingState.Loading;
        this.size = [w / 100, h / 100]; // scale down from pixels to meters

        await this.makePlane();
        this.pivot.add(this.plane);

        this.playAnimation();
        this.outputs.loadingState = LoadingState.Loaded;
      })
      .catch(() => {
        logger.error('Failed to init image');
        this.outputs.loadingState = LoadingState.Error;
      });
  }

  onDestroy() {
    if (this.plane) {
      disposeMesh(this.plane);
      this.plane = null;
    }
  }

  // Called back when any inputs properties change like localPosition or localRotation
  onInputsUpdated() {
    // the plane resets at this.makePlane();
    this.onInit();
    // this.playAnimation();
  }

  private async makePlane(): Promise<void> {
    // TODO: check on double model creation
    return new Promise((resolve, _reject) => {
      if (this.plane) {
        disposeMesh(this.plane);
        this.plane = null;
      }

      const [w, h] = this.size;
      const { width, height, url } = this.inputs;
      const THREE = this.context.three;
      const geometry = new THREE.PlaneGeometry(width || w, height || h, 10, 10);

      // TODO: move in instance TextureLoader
      // const _this = this;
      new THREE.TextureLoader().load(
        url,
        // onLoad callback
        (texture) => {
          try {
            if (texture != null && texture.isTexture) {
              // eslint-disable-next-line no-multi-assign
              texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
              texture.encoding = THREE.LinearEncoding;
              texture.repeat.set(1, 1);
              texture.needsUpdate = true;
              if (texture.image.width == 0 || texture.image.height == 0) {
                //console.error('Unable to get image resolution.',texture);
                throw new Error('Unable to get image resolution.');
              }
              //if for max tex Size
              if (
                texture.image.width > this.context.renderer.capabilities.maxTextureSize ||
                texture.image.height > this.context.renderer.capabilities.maxTextureSize
              ) {
                texture.image.height =
                  texture.image.height *
                  (texture.image.height / this.context.renderer.capabilities.maxTextureSize);
                texture.image.width =
                  texture.image.width *
                  (texture.image.width / this.context.renderer.capabilities.maxTextureSize);
              }
              // _this.context.renderer.initTexture(texture);
              const material = new THREE.MeshBasicMaterial({
                map: texture,
                side: THREE.DoubleSide,
                blending: THREE.NormalBlending,
                transparent: true,
              });
              this.plane = new THREE.Mesh(geometry, material);
              resolve();
            }
          } catch (e) {
            console.log('[Err] An error happened during texture init.', e);
          }
        },
        // onProgress callback currently not supported
        undefined,
        // onError callback
        function (err) {
          console.error('[Err] An error happened during texture load.', err);
        },
      );
    });
  }

  private playAnimation_old() {
    if (this.mixer) {
      const THREE = this.context.three;

      const position = this.plane?.position || new THREE.Vector3();
      const frame0 = position;
      const frame1 = new THREE.Vector3(position.x, position.y + 0.2, position.z);

      const track = new THREE.VectorKeyframeTrack(
        '.position',
        [0, this.inputs.period * 0.5, this.inputs.period],
        [frame0.x, frame0.y, frame0.z, frame1.x, frame1.y, frame1.z, frame0.x, frame0.y, frame0.z],
      );

      const clip = new THREE.AnimationClip(null, this.inputs.period, [track]);
      const action: any = this.mixer.clipAction(clip, this.plane);
      action.play();
    }
  }

  public playAnimation() {
    const THREE = this.context.three;

    if (!this.animationsManager) {
      this.animationsManager = new AnimationsManager(THREE, this.root.parent);
    }
    if (this.inputs.animationPulse) {
      this.animationsManager.pulse();
      this.animationsManager.play();
    } else if (this.inputs.animationUpDown) {
      this.animationsManager.bounceUpDown();
      this.animationsManager.play();
    } else if (this.inputs.animationFlip) {
      this.animationsManager.flip();
      this.animationsManager.play();
    } else {
      if (this.inputs.enableAnimation) {
        this.mixer = new THREE.AnimationMixer(this.plane);
        this.playAnimation_old();
      } else {
        this.mixer = null;
      }
    }
  }

  toggleHoverAnimation(value: boolean) {
    if (this.animationsManager && this.inputs.enableHoverAnimation) {
      if (value) {
        if (this.animationsManager) {
          this.animationsManager.stop();
          this.animationsManager.outlinePulse();
          this.animationsManager.play();
        }
      } else {
        this.animationsManager.stop();

        if (this.inputs.animationPulse) {
          this.animationsManager.pulse();
          this.animationsManager.play();
        } else if (this.inputs.animationUpDown) {
          this.animationsManager.bounceUpDown();
          this.animationsManager.play();
        } else if (this.inputs.animationFlip) {
          this.animationsManager.flip();
          this.animationsManager.play();
        }
      }
    }
  }

  stopAnimation() {
    if (this.animationsManager) {
      this.animationsManager.stop();
      this.animationsManager = null;
    }
  }

  onTick(delta: number) {
    if (this.animate && this.mixer) {
      this.mixer.update(delta / 1000);
    }
    if (this.animate && this.animationsManager) {
      this.animationsManager.update(delta);
    }
  }
}

export const imageComponentType = 'mp.imageComponent';
export const makeImageComponent = (): any => new ImageComponent();
